import qs from 'qs';

/**
 * Router search-param serializer with nested-object support.
 *
 * We use `qs` rather than `query-string` (which is flat-only) so routes can
 * namespace their params — e.g. `?filters[tier]=1&prefill[first_name]=Ada` —
 * instead of every concern (filters, form-prefill, dialogs, …) fighting over
 * the same flat top-level names. Namespacing lets otherwise-fine route overlaps
 * coexist without collisions.
 *
 * The config is chosen to stay byte-compatible with the previous
 * `query-string` serializer for FLAT urls, so existing links, redirects, and
 * `<Link>`/`navigate` call sites keep working unchanged:
 * - `arrayFormat: 'repeat'` → arrays serialize as repeated keys
 *   (`tier=1&tier=2`), matching query-string's `arrayFormat: 'none'`.
 * - `encodeValuesOnly: true` → keep structural `[brackets]` readable in the
 *   address bar while fully percent-encoding VALUES. This is what keeps the
 *   login `continue` param safe: it carries a whole app URL (its own `?`, `&`,
 *   `[`, `]`), and those are encoded as value content, so it round-trips intact
 *   through both `parseSearch` and the manual `URLSearchParams` fallback in the
 *   guest route.
 * - `sort` → canonical key order (query-string sorted by default) so a given
 *   search state always stringifies to the same URL.
 */
const STRINGIFY_OPTIONS: qs.IStringifyOptions = {
  arrayFormat: 'repeat',
  encodeValuesOnly: true,
  sort: (a, b) => a.localeCompare(b),
};

const PARSE_OPTIONS: qs.IParseOptions = {
  // Tolerate a leading `?` (TanStack may include it).
  ignoreQueryPrefix: true,
  // Bounds to keep parsing cheap and reject pathological deeply-nested input.
  // Match the parameter cap so valid repeated filters are not converted to
  // objects by qs's default 20-item array limit.
  arrayLimit: 100,
  depth: 5,
  parameterLimit: 100,
};

export function parseSearch(searchStr: string): Record<string, unknown> {
  return qs.parse(searchStr, PARSE_OPTIONS) as Record<string, unknown>;
}

export function stringifySearch(search: Record<string, unknown>): string {
  if (!search) {
    return '';
  }

  const searchString = qs.stringify(search, STRINGIFY_OPTIONS);

  return searchString ? `?${searchString}` : '';
}
